import React, {FC, useEffect, useRef} from 'react';
import styled, {css} from 'styled-components';

import {useInitialEffect} from '../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {usePrevious} from '../../../srcCommon/helpers/hookHelpers';
import {useAppDispatch} from '../../helpers/reduxHelpers';
import {SettingsDevelopersPagesEnum, SettingsDevelopersUrl} from '../../models/url/settingsUrlModel';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {closeSettingsDevelopersAction} from '../../store/panels/settings/settingsPanelActions';
import {SettingsDevelopersHeader} from './header/settingsDevelopersHeader';
import {SettingsDevelopersPages} from './pages/settingsDevelopersPages';

/*
 * Props.
 */

interface SettingsDevelopersProps {
  url: SettingsDevelopersUrl;
  panelId: SettingsPanelId;
  isApplicationLoading: boolean;
  localApplicationAsyncStatus?: AsyncStatusesEnum;
}

/*
 * Style.
 */

interface StyledSettingsDevelopersContentWrapperDiv {
  $isRenderingTabs: boolean;
}

const StyledSettingsDevelopersContentWrapperDiv = styled.div<StyledSettingsDevelopersContentWrapperDiv>`
  display: flex;
  padding: 0 48px;
  overflow: hidden;
  margin-top: -28px;
  padding-top: 28px;
  height: calc(100% - 82px);
  position: relative;
  min-width: 500px;

  ${(p) =>
    p.$isRenderingTabs &&
    css`
      height: calc(100% - 149px);
    `};
`;

/*
 * Component.
 */

export const SettingsDevelopers: FC<SettingsDevelopersProps> = (props) => {
  const {url, panelId} = props;
  const shouldRenderTabs = computeShouldRenderTabs(props);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const previousUrl = usePrevious(url);
  const dispatch = useAppDispatch();

  // If the subPage changes, make sure we scroll to the top of the next page.
  useEffect(() => {
    if (url.subPage !== previousUrl?.subPage && contentWrapperRef.current) {
      contentWrapperRef.current.scrollTo({top: 0});
    }
  }, [previousUrl?.subPage, url.subPage]);

  // When we leave developers, close it.
  useInitialEffect(() => () => {
    dispatch(closeSettingsDevelopersAction(panelId));
  });

  return (
    <>
      <SettingsDevelopersHeader url={url} shouldRenderTabs={shouldRenderTabs} />
      <StyledSettingsDevelopersContentWrapperDiv ref={contentWrapperRef} $isRenderingTabs={shouldRenderTabs}>
        <SettingsDevelopersPages panelId={panelId} url={url} />
      </StyledSettingsDevelopersContentWrapperDiv>
    </>
  );
};

/*
 * Helpers.
 */

function computeShouldRenderTabs(props: SettingsDevelopersProps) {
  const {url, isApplicationLoading, localApplicationAsyncStatus} = props;

  // If they are viewing an application and it is not loaded, no tabs.
  const isViewingApplication =
    url.subPage !== SettingsDevelopersPagesEnum.APPLICATIONS &&
    url.subPage !== SettingsDevelopersPagesEnum.TOKENS;
  // If we are viewing a single app and it is still loading.
  if (isViewingApplication && isApplicationLoading) {
    return false;
  }
  // We are viewing a single app and it failed.
  if (isViewingApplication && localApplicationAsyncStatus === AsyncStatusesEnum.FAILED) {
    return false;
  }

  // When viewing a token, we do not need tabs.
  if (url.subPage === SettingsDevelopersPagesEnum.TOKENS && url.tokenId) {
    return false;
  }
  // When viewing a specific feature, no tabs.
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_FEATURES && url.feature) {
    return false;
  }
  // When viewing a specific server or connecting, no tabs.
  if (url.subPage === SettingsDevelopersPagesEnum.APPLICATION_SERVERS && (url.serverId || url.isConnecting)) {
    return false;
  }
  return true;
}
